import { rgbToHex } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import HeatMap from "react-heatmap-grid";
import useWindowSize from "../../../custom_hook/useWindowSize";
import { 
  Box, 
  Typography, 
  Paper, 
  Chip, 
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const colorScale = ["#E6E6E6", "#CFCCCC", "#CCCBAD", "#808080", "#1A1A1A"];

// Styled components for enhanced visual appeal
const HeatMapContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: `${theme.spacing(3)} ${theme.spacing(3)} 0 0`
  }
}));

const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  background: alpha(theme.palette.background.default, 0.5),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  backdropFilter: 'blur(10px)'
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.spacing(1),
  background: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`
  }
}));

const ColorBox = styled(Box)<{ color: string; gradient?: boolean }>(({ theme, color, gradient }) => ({
  width: 60,
  height: 24,
  borderRadius: theme.spacing(0.5),
  border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  ...(gradient && {
    background: 'linear-gradient(90deg, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 1.0) 100%)'
  }),
  ...(!gradient && {
    backgroundColor: color
  })
}));

const StyledHeatMap = styled(Box)(({ theme }) => ({
  '& .react-heatmap-grid': {
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.08)}`
  },
  '& .react-heatmap-grid .cell': {
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      zIndex: 10,
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`
    }
  }
}));

// Function to convert a hex color to RGB
function hexToRgb(hex: any) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : {
        r: 230,
        g: 230,
        b: 230,
      };
}

function calculateRGB(value: any, min: any, max: any) {
  // Calculate the color index
  var colorIndex = Math.round(((value - min) / (max - min)) * (colorScale.length - 1));

  // Get the hex color
  var hexColor = colorScale[colorIndex];
  // Convert the hex color to RGB
  var rgbColor = hexToRgb(hexColor) as any;
  // Return the RGB color
  return `rgb(${rgbColor["r"]}, ${rgbColor["g"]},${rgbColor["b"]})`;
}

function HourHeatMap(props: any) {
  const theme = useTheme();
  const windowSize = useWindowSize();

  const [rect, setRect] = useState({ width: 0 }) as any;
  const [height, setHeight] = useState(0) as any;
  
  function dynamicHeatBox(width: any) {
    setHeight((width - 140) / 24);
  }
  
  const ref = useCallback(
    (node: any) => {
      if (node !== null) {
        setRect(node.getBoundingClientRect());
      }
    },
    [windowSize.width]
  );
  
  useEffect(() => {
    dynamicHeatBox(rect.width);
  }, [rect.width]);

  const xLabels = ["00:00", "", "", "", "", "", "06:00", "", "", "", "", "", "12:00", "", "", "", "", "", "18:00", "", "", "", "", "23:00"];
  const yLabels = props.labels ? props.labels : ["Sun", "", "", "Wed", "", "", "Sat"];
  const emptyData = new Array(yLabels.length).fill(0).map(() => new Array(xLabels.length).fill(0).map(() => 0));
  const data = props.data ? props.data : emptyData;
  const kwData = [] as any;
  data.map((e: any) => {
    e.map((i: any) => kwData.push(i.kw));
  });
  const minValue = Math.min(...kwData);
  const maxValue = Math.max(...kwData);

  return (
    <HeatMapContainer ref={ref}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ 
          fontWeight: 600,
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Vehicle Charging Heat Map
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Visualize parking and charging patterns throughout the week
        </Typography>
      </Box>

      <StyledHeatMap>
        <HeatMap
          height={height}
          squares={false}
          xLabels={xLabels}
          yLabels={yLabels}
          data={data}
          title={(value: any, unit: any) => {
            if (value.kw === 0 && value.vehicleCount > 0) {
              return `There ${value.vehicleCount > 1 ? "are" : "is"} ${value.vehicleCount} vehicle parking but not charging`;
            } else if (value.kw === 0) {
              return `There is no charging and parking vehicle `;
            } else {
              return `There ${value.vehicleCount > 1 ? "are" : "is"} ${value.vehicleCount} vehicles charging with ${value.kw.toFixed(1)} kilowatt per hour`;
            }
          }}
          cellRender={(value: any) => value.vehicleCount > 0 && (
            <Typography variant="body2" sx={{ 
              fontWeight: 700,
              color: 'inherit',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              fontSize: '1rem',
              lineHeight: 1,
              textAlign: 'center',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}>
              {value.vehicleCount}
            </Typography>
          )}
          cellStyle={(background: any, value: any, min: any, max: any, data: any, x: any, y: any) => {
            if (value.kw === 0 && value.vehicleCount > 0) {
              return {
                background: alpha(theme.palette.warning.main, 0.6),
                color: theme.palette.warning.contrastText,
                borderRadius: theme.spacing(0.5),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: 'pointer',
                minHeight: '100%',
                width: '100%',
                margin: '1px',
                position: 'relative'
              };
            } else if (value.kw === 0) {
              return {
                background: alpha(theme.palette.grey[400], 0.8),
                color: theme.palette.text.primary,
                borderRadius: theme.spacing(0.5),
                border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: 'pointer',
                minHeight: '100%',
                width: '100%',
                margin: '1px',
                position: 'relative'
              };
            } else {
              const intensity = (value.kw - minValue) / (maxValue - minValue);
              // Green gradient: light green (low) -> dark green (high)
              const greenIntensity = 0.3 + (intensity * 0.7); // 0.3 to 1.0 opacity
              const color = `rgba(76, 175, 80, ${greenIntensity})`;
              
              return {
                background: color,
                borderRadius: theme.spacing(0.5),
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: intensity > 0.5 ? '#ffffff' : '#000000',
                border: `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
                cursor: 'pointer',
                minHeight: '100%',
                width: '100%',
                margin: '1px',
                position: 'relative'
              };
            }
          }}
          xLabelWidth={0}
          xLabelTextAlign={"bottom"}
          yLabelTextAlign={"left"}
        />
      </StyledHeatMap>

      <Divider sx={{ my: 3 }} />

      <LegendContainer>
        <LegendItem>
          <ColorBox color={alpha(theme.palette.grey[400], 0.8)} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              No Activity
            </Typography>
          </Box>
        </LegendItem>

        <LegendItem>
          <ColorBox color={alpha(theme.palette.warning.main, 0.6)} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalParkingIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Parked (No Charging)
            </Typography>
          </Box>
        </LegendItem>

        <LegendItem>
          <ColorBox 
            color="transparent"
            gradient={true}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ElectricCarIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Charging ({Math.floor(minValue)}kW → {Math.round(maxValue)}kW)
            </Typography>
          </Box>
        </LegendItem>
      </LegendContainer>

      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Chip 
          icon={<Typography variant="caption">ℹ️</Typography>}
          label="Numbers represent vehicles currently parked"
          variant="outlined"
          sx={{ 
            background: alpha(theme.palette.info.main, 0.1),
            borderColor: alpha(theme.palette.info.main, 0.3),
            color: theme.palette.info.main
          }}
        />
      </Box>
    </HeatMapContainer>
  );
}

export default HourHeatMap;
